// List the authenticated user's own samples from Supabase.
//
// The companion to listMyPatches: surfaces every sample row you own,
// including ones uploaded externally (e.g. via the MCP server). The
// MINE/Cloud rail tab uses this to show samples that haven't been
// pulled into local IDB yet.
//
// RLS on the `samples` table joins through `patches.owner_id`; the
// explicit owner filter via `patches!inner(owner_id)` short-circuits
// the round-trip when the session is unauthenticated and mirrors the
// MCP's list_my_samples query so the two stay schema-aligned.

import { getSupabase } from '../auth/client';
import type { License } from '../samples/types';

export type MyCloudSample = {
  id: string;
  patchId: string;
  name: string;
  tags: string[];
  license: License;
  r2Key: string;
  durationMs: number;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
};

type Row = {
  id: string;
  patch_id: string;
  name: string;
  tags: string[] | null;
  license: License | null;
  r2_key: string;
  duration_ms: number;
  content_type: string;
  size_bytes: number;
  created_at: string;
  patches: { owner_id: string } | { owner_id: string }[] | null;
};

export async function listMyCloudSamples(): Promise<MyCloudSample[]> {
  const supabase = getSupabase();
  if (!supabase) return [];

  const { data: sessionData } = await supabase.auth.getSession();
  const session = sessionData.session;
  if (!session) return [];

  const { data, error } = await supabase
    .from('samples')
    .select(
      'id, patch_id, name, tags, license, r2_key, duration_ms, content_type, size_bytes, created_at, patches!inner(owner_id)',
    )
    .eq('patches.owner_id', session.user.id)
    .order('created_at', { ascending: false });
  if (error) throw new Error(`listMyCloudSamples failed: ${error.message}`);

  return ((data ?? []) as Row[]).map(rowToCloudSample);
}

function rowToCloudSample(row: Row): MyCloudSample {
  return {
    id: row.id,
    patchId: row.patch_id,
    name: row.name,
    tags: row.tags ?? [],
    license: row.license ?? 'unspecified',
    r2Key: row.r2_key,
    durationMs: row.duration_ms,
    contentType: row.content_type,
    sizeBytes: row.size_bytes,
    createdAt: row.created_at,
  };
}
